import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Package, User, Edit, LogOut, MapPin } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import LocationSelector from "@/components/LocationSelector";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function UserDashboard() {
  const navigate = useNavigate();
  const { token, user, logout, updateUserProfile, loading: authLoading } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({
    name: user?.name || "",
    email: user?.email || "",
    location: user?.location || { district: "", city: "", pincode: "", state: "Tamil Nadu" },
  });

  useEffect(() => {
    // Wait for auth loading to complete
    if (authLoading) return;
    
    if (!user || !token) {
      toast.error("Please login to access dashboard");
      navigate("/");
      return;
    }
    fetchOrders();
  }, [user, token, authLoading]);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders(response.data);
    } catch (error) {
      console.error("Failed to fetch orders:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    try {
      await updateUserProfile({ name: profileData.name, email: profileData.email, location: profileData.location });
      toast.success("Profile updated successfully");
      setEditingProfile(false);
    } catch (error) {
      toast.error("Failed to update profile");
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800",
      confirmed: "bg-blue-100 text-blue-800",
      processing: "bg-purple-100 text-purple-800",
      shipped: "bg-indigo-100 text-indigo-800",
      delivered: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };

  if (loading) {
    return (
      <div data-testid="user-dashboard-loading">
        <Navbar />
        <div className="min-h-screen flex items-center justify-center">
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="user-dashboard">
      <Navbar />

      <div className="min-h-screen py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-primary mb-8" data-testid="user-dashboard-title">
            My Dashboard
          </h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Profile Card */}
            <div className="lg:col-span-1">
              <div className="bg-card rounded-2xl p-6 shadow-soft sticky top-24" data-testid="user-profile-card">
                <div className="flex items-center space-x-3 mb-6">
                  <User className="w-6 h-6 text-primary" />
                  <h2 className="font-heading text-2xl font-bold">Profile</h2>
                </div>

                {editingProfile ? (
                  <form onSubmit={handleUpdateProfile} className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Name</label>
                      <Input
                        value={profileData.name}
                        onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                        data-testid="profile-name-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Email</label>
                      <Input
                        type="email"
                        value={profileData.email}
                        onChange={(e) => setProfileData({ ...profileData, email: e.target.value })}
                        data-testid="profile-email-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Phone</label>
                      <Input value={user.phone} disabled />
                    </div>
                    
                    {/* Location Selector */}
                    <LocationSelector
                      location={profileData.location}
                      onChange={(location) => setProfileData({ ...profileData, location })}
                      required={user.role === "vendor"}
                    />
                    
                    <div className="flex space-x-2">
                      <Button type="submit" className="flex-1 rounded-full" data-testid="profile-save-btn">
                        Save
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setEditingProfile(false)}
                        className="flex-1 rounded-full"
                        data-testid="profile-cancel-btn"
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                ) : (
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Name</p>
                      <p className="font-semibold" data-testid="profile-name-display">{user.name || "Not set"}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Email</p>
                      <p className="font-semibold" data-testid="profile-email-display">{user.email || "Not set"}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Phone</p>
                      <p className="font-semibold">{user.phone}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Role</p>
                      <p className="font-semibold capitalize">{user.role}</p>
                    </div>
                    {user.location && (
                      <div>
                        <p className="text-sm text-muted-foreground mb-1 flex items-center">
                          <MapPin className="w-4 h-4 mr-1" />
                          Location
                        </p>
                        <p className="font-semibold">
                          {user.location.city}, {user.location.district}
                          <br />
                          <span className="text-sm text-muted-foreground">{user.location.pincode}, Tamil Nadu</span>
                        </p>
                      </div>
                    )}
                    <Button
                      onClick={() => setEditingProfile(true)}
                      variant="outline"
                      className="w-full rounded-full"
                      data-testid="profile-edit-btn"
                    >
                      <Edit className="w-4 h-4 mr-2" />
                      Edit Profile
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Orders */}
            <div className="lg:col-span-2">
              <div className="bg-card rounded-2xl p-6 shadow-soft" data-testid="user-orders-section">
                <div className="flex items-center space-x-3 mb-6">
                  <Package className="w-6 h-6 text-primary" />
                  <h2 className="font-heading text-2xl font-bold">My Orders</h2>
                </div>

                {orders.length === 0 ? (
                  <div className="text-center py-12" data-testid="orders-empty">
                    <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                    <p className="text-muted-foreground mb-4">No orders yet</p>
                    <Button onClick={() => navigate("/products")} className="rounded-full" data-testid="orders-empty-shop-btn">
                      Start Shopping
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4" data-testid="orders-list">
                    {orders.map((order) => (
                      <div key={order.id} className="border border-border rounded-xl p-4" data-testid="order-card">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <p className="font-semibold text-sm text-muted-foreground" data-testid="order-id">
                              Order #{order.id.slice(0, 8)}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(order.created_at).toLocaleDateString()}
                            </p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`} data-testid="order-status">
                            {order.status}
                          </span>
                        </div>
                        <div className="space-y-2 mb-3">
                          {order.items.map((item, idx) => (
                            <div key={idx} className="flex justify-between text-sm">
                              <span className="text-muted-foreground">
                                {item.product_name} x {item.quantity}
                              </span>
                              <span className="font-semibold">₹{(item.price * item.quantity).toFixed(2)}</span>
                            </div>
                          ))}
                        </div>
                        <div className="flex justify-between items-center pt-3 border-t border-border">
                          <span className="font-semibold">Total</span>
                          <span className="font-heading text-xl font-bold text-primary" data-testid="order-total">
                            ₹{order.total_amount.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
